<template>
  <div
    :class="[
      $style.actionTypeMessagePreview,
      {
        [$style.actionTypeMessagePreviewIsDark]: isDark,
        [$style.actionTypeMessagePreviewIsMobile]: previewType === 'mobile',
      }
    ]"
  >
    <ActionTypeWidgetPreview :redirect="redirect">
      <div :class="$style.PFMessageCtx">
        <section :class="$style.PFMessageBody">
          <template v-if="title !== null">
            <header :class="$style.PFMessageHeader">
              <div :class="$style.PFMessageHeaderGroup">
                <img
                  v-if="avatarEnabled"
                  :src="avatarPreview"
                  alt=""
                  :class="$style.PFMessageAvatar"
                >
                <span
                  :class="[
                    $style.PFMessageHeaderText,
                    {
                      [$style.PFMessageHeaderTextWithout]: !avatarEnabled,
                    }
                  ]"
                >
                  {{ title }}
                </span>
              </div>
              <div>
                <button :class="$style.PFMessageButtonClose">
                  <CloseRounded :class="$style.PFMessageButtonCloseIcon" />
                </button>
              </div>
            </header>
            <div :class="$style.PFMessageLine" />
          </template>

          <div :class="$style.PFMessageMain">
            <span
              :class="[
                $style.PFMessageMainText,
                {
                  [$style.PFMessageMainTextWithButton]: title === null,
                }
              ]"
            >
              {{ text }}
            </span>
            <div v-if="title === null">
              <button :class="$style.PFMessageButtonClose">
                <CloseRounded :class="$style.PFMessageButtonCloseIcon" />
              </button>
            </div>
          </div>

          <footer
            v-if="branding"
            :class="$style.PFMessageFooter"
          >
            Made on Yourgood Widget
          </footer>
        </section>
        <ShapeForMessage :class="$style.PFMessageShape" />
      </div>
    </ActionTypeWidgetPreview>
  </div>
</template>

<script>
/**
 * UI
 */
import ActionTypeWidgetPreview from '@/components/Actions/ActionPreview/ActionTypeWidgetPreview.vue'
/**
 * Static
 */
import ShapeForMessage from '@/assets/icons/ShapeForMessage.svg?inline'
import CloseRounded from '@/assets/icons/CloseRounded.svg?inline'

export default {
  components: {
    ActionTypeWidgetPreview,
    ShapeForMessage,
    CloseRounded
  },
  props: {
    previewType: {
      type: String,
      default: 'desktop'
    },
    redirect: {
      type: String,
      default: 'whatsapp'
    },
    avatarEnabled: {
      type: Boolean,
      default: false
    },
    avatar: {
      type: [
        Object,
        process.client ? window.File : Object
      ],
      default: null
    },
    title: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: ''
    },
    isDark: {
      type: Boolean,
      default: false
    },
    branding: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    avatarPreview () {
      if (!this.avatar) { return null }
      return this.avatar instanceof File
        ? URL.createObjectURL(this.avatar)
        : this.avatar.url
    }
  }
}
</script>

<style module lang="scss">
@use '@/assets/scss/mixins';

.actionTypeMessagePreview {
  --s: 56px;

  all: unset;
  display: block;
  width: var(--s);
  height: var(--s);
  position: absolute;
  bottom: 40px;
  right: 40px;
  border-radius: 50%;

  &.actionTypeMessagePreviewIsMobile {
    bottom: 16px;
    right: 16px;

    .PFMessageCtx {
      transform: scale(0.65);
    }
  }

  &:not(.actionTypeMessagePreviewIsDark) {
    .PFMessageCtx {
      background-color: var(--white);
    }

    .PFMessageShape {
      color: var(--white);
    }

    .PFMessageFooter {
      color: var(--grey-500);
    }

    .PFMessageButtonClose {
      color: var(--grey-400);
    }

    .PFMessageMainText,
    .PFMessageHeaderText {
      color: var(--primary);
    }

    .PFMessageLine {
      background-color: var(--grey-200);
    }

    .PFMessageAvatar {
      background-color: var(--grey-200);
    }
  }

  &.actionTypeMessagePreviewIsDark {
    .PFMessageCtx {
      background-color: var(--grey-900);
    }

    .PFMessageShape {
      color: var(--grey-900);
    }

    .PFMessageFooter {
      color: var(--disabled);
    }

    .PFMessageButtonClose {
      color: var(--grey-700);
    }

    .PFMessageMainText,
    .PFMessageHeaderText {
      color: var(--white);
    }

    .PFMessageLine {
      background-color: var(--grey-800);
    }

    .PFMessageAvatar {
      background-color: var(--grey-800);
    }
  }
}

.PFMessageCtx {
  all: unset;
  box-sizing: border-box;
  display: flex;
  position: absolute;
  bottom: 50%;
  right: calc(100% + 24px);
  border-radius: 8px;
  filter: drop-shadow(0 10px 20px rgba(33, 33, 33, 0.04)) drop-shadow(0 20px 40px rgba(33, 33, 33, 0.04));
  transform-origin: right bottom;
}

.PFMessageBody {
  all: unset;
  box-sizing: border-box;
  width: 324px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.PFMessageShape {
  all: unset;
  height: 14px;
  width: 10px;
  position: absolute;
  right: -8px;
  bottom: 7px;
}

.PFMessageButtonClose {
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.PFMessageButtonCloseIcon {
  --s: 24px;

  all: unset;
  display: flex;
  width: var(--s);
  height: var(--s);
  color: currentColor;
}

.PFMessageMain {
  all: unset;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.PFMessageMainText {
  all: unset;
  display: inline-block;
  max-width: 100%;
  word-wrap: break-word;

  @include mixins.subtitle1;

  &.PFMessageMainTextWithButton {
    max-width: calc(100% - 32px);
  }
}

.PFMessageHeader {
  all: unset;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.PFMessageLine {
  all: unset;
  display: flex;
  width: 100%;
  height: 1px;
}

.PFMessageHeaderGroup {
  all: unset;
  display: flex;
  align-items: center;
  gap: 16px;
}

.PFMessageAvatar {
  --s: 56px;

  all: unset;
  display: flex;
  width: var(--s);
  height: var(--s);
  border-radius: 50%;
  overflow: hidden;
  object-fit: cover;
  flex-shrink: 0;
}

.PFMessageHeaderText {
  all: unset;
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  text-overflow: ellipsis;
  width: 185px;
  overflow: hidden;
  word-wrap: anywhere !important;

  &.PFMessageHeaderTextWithout {
    width: 257px;
  }

  @include mixins.h2;
}

.PFMessageFooter {
  all: unset;
  width: 100%;
  display: inline-block;
  text-align: center;

  @include mixins.inputLabel;
}

.PFMessageCtx,
.PFMessageLine,
.PFMessageAvatar {
  transition: background-color 0.3s;
}

.PFMessageShape,
.PFMessageMainText,
.PFMessageHeaderText,
.PFMessageButtonClose,
.PFMessageFooter {
  transition: color 0.3s;
}
</style>
